import React, { useState, useEffect } from "react";
import { useAuth } from "../../../../../hooks/useAuth";
import {
  patchAdExtensionAPI,
  sendAdminNotificationAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import "../AdDetailModal.css";
import "./QuantityIncreaseModal.css";

const QuantityIncreaseModal = ({
  isOpen,
  onClose,
  adData,
  unitPrice, // 1회 단가
  onSave, // 저장 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const [newDailyQuantity, setNewDailyQuantity] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [inputError, setInputError] = useState("");

  // 현재 dailyQuantity 계산
  const getCurrentDailyQuantity = () => {
    let dailyQuantity = adData.dailyQuantity || 0;
    if (
      !dailyQuantity &&
      adData.totalQuantity &&
      adData.startDate &&
      adData.endDate
    ) {
      // totalQuantity와 광고 기간으로 역산
      const startDate = new Date(adData.startDate.split("T")[0]);
      const endDate = new Date(adData.endDate.split("T")[0]);
      const totalDays =
        Math.ceil(
          (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24)
        ) + 1;
      if (totalDays > 0) {
        dailyQuantity = Math.round(adData.totalQuantity / totalDays);
      }
    }
    return dailyQuantity;
  };

  const currentDailyQuantity = getCurrentDailyQuantity();
  const minDailyQuantity = Math.max(100, currentDailyQuantity); // 최소값은 현재 dailyQuantity와 100 중 큰 값

  // 모달이 열릴 때 초기값 설정
  useEffect(() => {
    if (isOpen) {
      setNewDailyQuantity(minDailyQuantity.toString());
      setInputError("");
    }
  }, [isOpen, minDailyQuantity]);

  // 날짜 포맷팅 함수 (YYYY-MM-DD -> YYYY.MM.DD)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";
    return dateString.replace(/-/g, ".");
  };

  // 오늘부터 endDate까지의 일수 계산 (년월일만 사용)
  const calculateRemainingDays = () => {
    if (!adData.endDate) return 0;

    // 오늘 날짜 (로컬 시간 기준, 년월일만)
    // new Date()는 이미 로컬 시간이므로 그대로 사용
    const now = new Date();
    const todayYear = now.getFullYear();
    const todayMonth = now.getMonth();
    const todayDate = now.getDate();
    const today = new Date(todayYear, todayMonth, todayDate);

    // endDate는 UTC로 저장되어 있으므로 한국시간으로 변환
    // UTC 2025-11-05T14:59:59.000Z → 한국시간 2025-11-05 23:59:59
    const utcEndDate = new Date(adData.endDate);
    const koreaEndDate = new Date(utcEndDate.getTime() + 9 * 60 * 60 * 1000); // UTC+9

    // 년월일만 추출
    const endYear = koreaEndDate.getFullYear();
    const endMonth = koreaEndDate.getMonth();
    const endDay = koreaEndDate.getDate();
    const endDateOnly = new Date(endYear, endMonth, endDay);

    // 년월일만 비교해서 일수 계산
    // 오늘이 11-04이고 종료일이 11-05라면: 11-04(오늘), 11-05(종료일) = 2일
    const diffTime = endDateOnly.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24); // 밀리초를 일수로 변환

    // 오늘부터 종료일까지 포함한 일수 (오늘 포함, 종료일 포함)
    // diffDays가 0이면 같은 날 = 1일
    // diffDays가 1이면 다음날까지 = 2일
    const remainingDays = Math.max(1, Math.floor(diffDays) + 1);

    return remainingDays;
  };

  const remainingDays = calculateRemainingDays();

  // 입력값을 숫자로 변환
  const parsedNewDailyQuantity = parseInt(newDailyQuantity) || 0;

  // 추가 수량 계산
  const calculateAdditionalQuantity = () => {
    if (parsedNewDailyQuantity < minDailyQuantity) return 0;
    const additionalDailyQuantity =
      parsedNewDailyQuantity - currentDailyQuantity;
    return additionalDailyQuantity * remainingDays;
  };

  const additionalQuantity = calculateAdditionalQuantity();

  // 추가 금액 계산
  const calculateAdditionalAmount = () => {
    return additionalQuantity * unitPrice;
  };

  const additionalAmount = calculateAdditionalAmount();

  // 입력값 검증
  const validateInput = (value) => {
    if (value === "") {
      setInputError("");
      return true;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < minDailyQuantity) {
      setInputError(
        `일일 수량은 최소 ${minDailyQuantity}회 이상이어야 합니다.`
      );
      return false;
    }

    setInputError("");
    return true;
  };

  // 입력 핸들러
  const handleInputChange = (e) => {
    const value = e.target.value;

    // 빈 값 또는 숫자만 허용
    if (value === "" || /^\d+$/.test(value)) {
      setNewDailyQuantity(value);
      validateInput(value);
    }
  };

  // 포커스 아웃 시 검증
  const handleInputBlur = () => {
    if (newDailyQuantity === "" || parsedNewDailyQuantity < minDailyQuantity) {
      setNewDailyQuantity(minDailyQuantity.toString());
      setInputError("");
    }
  };

  // 한국 시간 포맷 (YYYY-MM-DD HH:mm:ss)
  const getKoreaTimeString = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9

    const year = koreaTime.getFullYear();
    const month = String(koreaTime.getMonth() + 1).padStart(2, "0");
    const day = String(koreaTime.getDate()).padStart(2, "0");
    const hours = String(koreaTime.getHours()).padStart(2, "0");
    const minutes = String(koreaTime.getMinutes()).padStart(2, "0");
    const seconds = String(koreaTime.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  // 수량 증가 처리
  const handleIncrease = async () => {
    if (parsedNewDailyQuantity < minDailyQuantity) {
      alert(
        `일일 수량은 최소 ${minDailyQuantity} 이상이어야 합니다. (현재: ${currentDailyQuantity})`
      );
      return;
    }

    if (parsedNewDailyQuantity === currentDailyQuantity) {
      alert("변경된 수량이 없습니다.");
      return;
    }

    if (additionalAmount > (user?.deposit || 0)) {
      alert("예치금이 부족합니다.");
      return;
    }

    const confirmMessage = `일일 수량을 ${currentDailyQuantity}에서 ${parsedNewDailyQuantity}로 증가시키겠습니까?\n\n추가 수량: ${additionalQuantity.toLocaleString()}회\n추가 금액: ${additionalAmount.toLocaleString()}원`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsProcessing(true);

      // 기존 데이터
      const currentTotalQuantity = adData.totalQuantity || 0;
      const currentPrice = adData.price || 0;
      const currentDeposit = user?.deposit || 0;

      // 새로운 데이터
      const newTotalQuantity = currentTotalQuantity + additionalQuantity;
      const newPrice = currentPrice + additionalAmount;
      const newDeposit = currentDeposit - additionalAmount;

      // todayRemainingCount 계산
      // missionTemplate이 4, 5, 6일 때는 newTotalQuantity 사용
      // 그 외에는 원래 남아있던 수량 + 추가된 하루 수량
      const missionTemplate =
        adData.missionTemplate || adData.mission_template_id;
      let newTodayRemainingCount;

      if (missionTemplate === 5 || missionTemplate === 6) {
        // missionTemplate 4, 5, 6: 추가된 총 수량 사용
        newTodayRemainingCount = newTotalQuantity;
      } else {
        // 그 외: 원래 남아있던 수량 + 추가된 하루 수량
        const originalTodayRemainingCount =
          adData.todayRemainingCount || currentDailyQuantity;
        const additionalDailyQuantity =
          parsedNewDailyQuantity - currentDailyQuantity;
        newTodayRemainingCount =
          originalTodayRemainingCount + additionalDailyQuantity;
      }

      // history JSON 문자열 생성
      const historyKey = getKoreaTimeString();
      const historyValue = {
        before: {
          totalQuantity: currentTotalQuantity,
          dailyQuantity: currentDailyQuantity,
          price: currentPrice,
        },
        after: {
          totalQuantity: newTotalQuantity,
          dailyQuantity: parsedNewDailyQuantity,
          price: newPrice,
        },
        additionalAmount: additionalAmount,
      };
      const historyJson = JSON.stringify({
        [historyKey]: historyValue,
      });

      // API 호출 데이터 구성
      const extensionData = {
        ad_id: adData.ad_id || adData.id,
        totalQuantity: newTotalQuantity,
        dailyQuantity: parsedNewDailyQuantity,
        history: historyJson,
        todayRemainingCount: newTodayRemainingCount,
        updateDeposit: {
          deposit: newDeposit,
          price: newPrice,
          logs: {
            balance: newDeposit,
            amount: -additionalAmount,
            admin_id: 1,
            title: `수량 추가(${
              adData.missionName || adData.adName || "광고"
            }) ${currentDailyQuantity} -> ${parsedNewDailyQuantity}`,
          },
        },
      };

      // API 호출
      await patchAdExtensionAPI(extensionData);

      //   // 관리자 알림 전송
      try {
        const notificationData = {
          title: "광고 수량 추가",
          content: `수량 추가(${
            adData.missionName || adData.adName || "광고"
          }) ${currentDailyQuantity} -> ${parsedNewDailyQuantity}`,
        };
        await sendAdminNotificationAPI(notificationData);
      } catch (notificationError) {
        console.error("관리자 알림 전송 실패:", notificationError);
        // 알림 실패는 수량 증가 실패로 처리하지 않음 (로깅만)
      }

      alert("수량 증가가 성공적으로 완료되었습니다.");
      onClose();
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("수량 증가 실패:", error);
      alert(
        `수량 증가에 실패했습니다: ${
          error.response?.data?.msg || error.message || "알 수 없는 오류"
        }`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quantity-increase-modal-overlay" onClick={onClose}>
      <div
        className="quantity-increase-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="quantity-increase-modal-header">
          <h2>수량 증가</h2>
          <button
            className="quantity-increase-modal-close-btn"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* 내용 */}
        <div className="quantity-increase-modal-content">
          {/* 예치금 정보 */}
          <div className="quantity-increase-deposit-info">
            <span className="quantity-increase-deposit-label">예치금:</span>
            <span className="quantity-increase-deposit-amount">
              {(user?.deposit || 0).toLocaleString()} 원
            </span>
          </div>

          {/* 광고 정보 */}
          <div className="quantity-increase-ad-info">
            <div className="quantity-increase-info-row">
              <label>광고명</label>
              <div>{adData.missionName || "-"}</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>현재 일일 수량</label>
              <div>{currentDailyQuantity.toLocaleString()}회</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>광고 종료일</label>
              <div>
                {adData.endDate
                  ? formatDateForDisplay(adData.endDate.split("T")[0])
                  : "-"}
              </div>
            </div>
            <div className="quantity-increase-info-row">
              <label>잔여 일수</label>
              <div>{remainingDays}일</div>
            </div>
            <div className="quantity-increase-info-row">
              <label>1회 단가</label>
              <div>{unitPrice.toLocaleString()}원</div>
            </div>
          </div>

          {/* 수량 입력 */}
          <div className="quantity-increase-input-section">
            <label htmlFor="newDailyQuantity">
              새로운 일일 수량 (최소: {minDailyQuantity}회)
            </label>
            <input
              id="newDailyQuantity"
              type="text"
              inputMode="numeric"
              value={newDailyQuantity}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={`최소 ${minDailyQuantity}회 이상 입력`}
              className={`quantity-increase-input ${
                inputError ? "quantity-increase-input-error" : ""
              }`}
            />
            {inputError && (
              <div className="quantity-increase-error">{inputError}</div>
            )}
          </div>

          {/* 계산 결과 */}
          {parsedNewDailyQuantity >= minDailyQuantity &&
            parsedNewDailyQuantity > currentDailyQuantity && (
              <div className="quantity-increase-result">
                <div className="quantity-increase-result-row">
                  <label>추가 일일 수량</label>
                  <div>
                    {(
                      parsedNewDailyQuantity - currentDailyQuantity
                    ).toLocaleString()}
                    회
                  </div>
                </div>
                <div className="quantity-increase-result-row">
                  <label>총 추가 수량</label>
                  <div>{additionalQuantity.toLocaleString()}회</div>
                </div>
                <div className="quantity-increase-result-row highlight">
                  <label>추가 금액</label>
                  <div>{additionalAmount.toLocaleString()}원</div>
                </div>
              </div>
            )}
        </div>

        {/* 푸터 */}
        <div className="quantity-increase-modal-footer">
          <button
            className="quantity-increase-cancel-btn"
            onClick={onClose}
            disabled={isProcessing}
          >
            취소
          </button>
          <button
            className="quantity-increase-confirm-btn"
            onClick={handleIncrease}
            disabled={
              isProcessing ||
              parsedNewDailyQuantity < minDailyQuantity ||
              parsedNewDailyQuantity === currentDailyQuantity ||
              additionalAmount > (user?.deposit || 0) ||
              inputError !== ""
            }
          >
            {isProcessing ? "처리 중..." : "확인"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuantityIncreaseModal;
